import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective, ValidatorFn, Validators } from '@angular/forms';
import { Currencies } from '../../../../../core/constants/currency';
import { IbanRegex } from '../../../../../core/constants/ibanRegex';
import { Permissions } from '../../../../../core/constants/permissions';
import { BankAccount } from '../../../../../core/models/bank-account.model';
import { BankTransferMethod, CaxtonTitle, FullPatientPaymentInfo, PaymentMethod } from '../../../../../core/models/patient.model';
import { BankRequirements, CountryInfo } from '../../../../../core/models/payment.models';
import { SelectOption } from '../../../../../core/models/select-option.model';
import { AuthService } from '../../../../../core/services/auth.service';
import { PatientService } from '../../../../../core/services/patient.service';
import { PaymentService } from '../../../../../core/services/payment.service';
import { SortCodeValidator } from '../../../../../core/validators/sort-code.validator';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { AutosuggestDropdownInputComponent } from '../../../../../shared/autosuggest-dropdown-input/autosuggest-dropdown-input.component';
import { DropdownInputComponent } from '../../../../../shared/dropdown-input/dropdown-input.component';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-payment-info-caxton-bank',
  templateUrl: './payment-info-caxton-bank.component.html',
  styleUrls: ['./payment-info-caxton-bank.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class PaymentInfoCaxtonBankComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("titleSelect") titleSelect: DropdownInputComponent;
  @ViewChild("countrySelect") countrySelect: AutosuggestDropdownInputComponent;
  @ViewChild("currencySelect") currencySelect: AutosuggestDropdownInputComponent;

  @Input() patientPaymentInfo: FullPatientPaymentInfo;
  @Input() paymentInfoForm: FormGroup;
  @Input() trialId: string;
  @Input() patientTrialId: string;
  @Input() bankRequirements: BankRequirements;
  @Input() previousBeneficiaryIds: string[];

  titleOptions: SelectOption[] = CaxtonTitle.all().map((x) => ({ value: x, text: x }));
  currencies: SelectOption[] = Currencies.all().map((x) => ({ value: x.cc, text: `${x.name} (${x.cc})` }));
  countries: SelectOption[] = [];
  countriesList: CountryInfo[] = [];
  updateBeneficiary: boolean = false;
  invalidForm: boolean = false;
  currentBeneficiaryId: string;
  showPreviousBeneficiaries: boolean = false;
  currency: string;

  bankAccount = new BankAccount({ bankAccountEnabled: false });

  Permissions = Permissions;

  private destroyed$ = new Subject<void>();

  constructor(
    private cd: ChangeDetectorRef,
    private alertService: AlertService,
    private paymentService: PaymentService,
    private patientService: PatientService,
    public authService: AuthService) { }

  ngOnInit() {
    this.initiateCountries();
    this.subscribeCountryValueChanges();
    this.subscribeBankCoverFeeCurrencyValueChanges();

    this.currency =
      this.patientPaymentInfo.defaultPaymentMethod === PaymentMethod.BankTransfer &&
        this.patientPaymentInfo.bankTransferMethod === BankTransferMethod.Automated
        ? this.patientPaymentInfo.beneficiaryDetails?.currency
        : this.patientPaymentInfo.bankCoverFeeCurrency;
  }

  ngAfterViewInit(): void {
    this.loadBankAccount();

    this.currencySelect.setSelectedOption(this.paymentInfoForm.get('currency').value.toString());
    this.titleSelect.setValue(this.paymentInfoForm.get('title').value?.toString());

    this.subscribeCurrencyValueChanges();

    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  subscribeBankCoverFeeCurrencyValueChanges() {
    this.paymentInfoForm.get('bankCoverFeeCurrency').valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((value) => {
        this.currency = value;
      });
  }

  subscribeCurrencyValueChanges() {
    this.paymentInfoForm.get('currency').valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((value) => {
        if (!this.paymentInfoForm.get('country').value) {
          return;
        }

        this.paymentService.getBankRequirements(this.trialId, this.paymentInfoForm.get('country').value, value).subscribe((response) => {
          Object.assign(this.bankRequirements, response);

          this.setValidation('sortCode', response.usesSortCode, [Validators.required, SortCodeValidator()]);
          this.setValidation('swift', response.usesSwift, [Validators.required, Validators.pattern(/^([a-zA-Z]){4}([a-zA-Z]){2}([0-9a-zA-Z]){2}([0-9a-zA-Z]{3})?$/)]);
          this.setValidation('accountNumber', response.usesSortCode || response.usesSwift, [Validators.required]);
          this.setValidation('iban', response.usesIban, [Validators.required, Validators.pattern(IbanRegex.all()[this.paymentInfoForm.get('country').value]?.regex)]);

          this.cd.detectChanges();
        });
      });
  }

  loadBankAccount(): void {
    this.patientService.bankAccount(this.patientTrialId).subscribe({
      next: (bankAccount) => {
        this.bankAccount = bankAccount;
      },
      error: (error) => {
        this.alertService.showErrorAlert(error);
      },
    });
  }

  initiateCountries() {
    this.paymentService.getCountries(this.trialId).subscribe({
      next: (response) => {
        this.countries = response.map((x) => ({ value: x.code, text: x.name }));
        this.countriesList = response;

        setTimeout(() => {
          this.countrySelect.setSelectedOption(this.paymentInfoForm.get('country').value);
        }, 100);
      },
      error: () => {
        this.alertService.showWarningAlert("Failed to get countries. Please try again or contact an administrator");
      },
    });
  }

  private subscribeCountryValueChanges() {
    this.paymentInfoForm.get('country').valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((value) => {
        const currency = this.countriesList.find((x) => x.code === value)?.currencyCode;

        if (this.paymentInfoForm.get('currency').value !== currency) {
          this.paymentInfoForm.patchValue({ currency }, { emitEvent: true });
        }
        
        this.currency = currency;
      });
  }

  editBeneficiary() {
    this.currentBeneficiaryId = this.paymentInfoForm.get("beneficiaryId").value;
    this.paymentInfoForm.get("beneficiaryId").setValue("");

    this.paymentInfoForm.get("country").enable();
    this.paymentInfoForm.get("currency").enable();
    this.paymentInfoForm.get("firstName").enable();
    this.paymentInfoForm.get("lastName").enable();
    this.paymentInfoForm.get("iban").enable();
    this.paymentInfoForm.get("sortCode").enable();
    this.paymentInfoForm.get("accountNumber").enable();
    this.paymentInfoForm.get("swift").enable();
    this.paymentInfoForm.get("accountOwnerAddress").enable();

    this.paymentInfoForm.get("title").setValidators(Validators.required);
    this.paymentInfoForm.get("firstName").setValidators([Validators.required, Validators.pattern('^[A-Za-z ]+$')]);
    this.paymentInfoForm.get("lastName").setValidators([Validators.required, Validators.pattern('^[A-Za-z ]+$')]);

    this.setValidation("sortCode", this.bankRequirements.usesSortCode, [Validators.required, SortCodeValidator()]);
    this.setValidation("swift", this.bankRequirements.usesSwift, [Validators.required, Validators.pattern(/^([a-zA-Z]){4}([a-zA-Z]){2}([0-9a-zA-Z]){2}([0-9a-zA-Z]{3})?$/)]);
    this.setValidation("accountNumber", this.bankRequirements.usesSortCode || this.bankRequirements.usesSwift, [Validators.required]);
    this.setValidation("iban", this.bankRequirements.usesIban, [Validators.required, Validators.pattern(IbanRegex.all()[this.paymentInfoForm.get('country').value]?.regex)]);

    this.paymentInfoForm.get("title").updateValueAndValidity();
    this.updateBeneficiary = true;
    this.invalidForm = false;
  }

  setValidation(controlName: string, condition: boolean, validators: ValidatorFn[]) {
    const control = this.paymentInfoForm.get(controlName);

    if (condition) {
      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });
    } else {
      control.clearValidators();
      control.setErrors(null);
      control.setValue('', { emitEvent: false });
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  togglePreviousBeneficiaries() {
    this.showPreviousBeneficiaries = !this.showPreviousBeneficiaries;
  }
}
